import {
  DeviceWithInstalledPower,
  getMinDate,
  initializeFields,
  useLimits,
  useTemplatedForecastDataMutation,
} from '@e2m-customer-portal/domains';
import { reduceForecastTemplateLimits, useLocale, usePowerLevels, useSnackbar } from '@e2m-customer-portal/hooks';
import { convertMeasure, isMeasureWithValue, Measure, NullableMeasure } from '@e2m-customer-portal/shared';
import { DateRangePicker } from '@e2m-customer-portal/ui';
import { Button, CircularProgress } from '@mui/material';
import clsx from 'clsx';
import {
  addSeconds,
  eachDayOfInterval,
  endOfDay,
  endOfToday,
  Interval,
  isBefore,
  setDay,
  startOfDay,
  startOfToday,
  subSeconds,
  toDate,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'react-use';
import { CheckCircleIcon, ClearIcon, EditIcon } from '../../../../../../assets/icons';
import {
  ForecastProperty,
  InputFieldType,
  MarketLocation,
  Resolution,
  Weekday,
} from '../../../../../../generated/graphql';
import { getErrorTranslationKey } from '../../../../../../utils';
import { TimePickerMobile } from './TimePickerMobile';
import { ValuePickerMobile } from './ValuePickerMobile';

type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const weekdays = {
  0: Weekday.SUNDAY,
  1: Weekday.MONDAY,
  2: Weekday.TUESDAY,
  3: Weekday.WEDNESDAY,
  4: Weekday.THURSDAY,
  5: Weekday.FRIDAY,
  6: Weekday.SATURDAY,
};

const convertedValue = (measure: NullableMeasure) => convertMeasure(measure, 'kW').value;

interface ForecastAssistantMobileProps {
  marketLocation: MarketLocation;
  device: DeviceWithInstalledPower;
  interval: Interval;
  isFifteenMinutesResolution?: boolean;
  visible?: boolean;
  allowSliceSelection: boolean;
  selectionModeHasBeenToggled?: (isInSliceSelectionMode: boolean) => void;
  sliceSelection: Array<number>;
  assistantDate: Date;
  currentForecastProperty?: ForecastProperty;
}

export function ForecastAssistantMobile({
  marketLocation,
  device,
  interval: tableInterval,
  isFifteenMinutesResolution,
  visible,
  allowSliceSelection,
  selectionModeHasBeenToggled,
  sliceSelection,
  assistantDate,
  currentForecastProperty,
}: Readonly<ForecastAssistantMobileProps>): ReactElement<ForecastAssistantMobileProps> {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const { showSuccess, showError } = useSnackbar();

  const [isOpen, setIsOpen] = useState(false);
  const open = () => {
    setIsOpen(true);
    window.document.body.style.overflow = 'hidden';
  };
  const close = () => {
    setIsOpen(false);
    window.document.body.style.overflow = 'unset';

    // Reset the assistant
    setDateRange({ start: startOfToday(), end: endOfToday() });
    setTimeRange({
      start: new Date('1970-01-02T00:00'),
      end: new Date('1970-01-02T00:00'),
    });
    setProduction({ value: installedPower, unit: 'kW' });
  };

  const [isInSliceSelectionMode, toggleIsInSliceSelectionMode] = useToggle(false);
  const toggleSelectionMode = () => {
    if (selectionModeHasBeenToggled) selectionModeHasBeenToggled(!isInSliceSelectionMode);
    toggleIsInSliceSelectionMode();
  };

  const [dateRange, setDateRange] = useState<Interval>({ start: startOfToday(), end: endOfToday() });
  const [timeRange, setTimeRange] = useState<Interval>({
    start: new Date('1970-01-02T00:00'),
    end: new Date('1970-01-02T00:00'),
  });
  const setStart = (start: string | number | Date) => setTimeRange({ ...timeRange, start });
  const setEnd = (end: string | number | Date) => setTimeRange({ ...timeRange, end });

  const firstHour = toDate(timeRange.start).getHours();
  const hourCount = (toDate(timeRange.end).getHours() || 24) - firstHour;
  const hourSelection =
    hourCount >= 0
      ? Array(hourCount)
          .fill(firstHour)
          .map((start: number, offset) => start + offset)
      : [];

  const availableWeekdays = useMemo(
    () => new Set(eachDayOfInterval(dateRange).map((day) => day.getDay() as DayOfWeek)),
    [dateRange],
  );
  const [selectedWeekdays, setSelectedWeekdays] = useState(availableWeekdays);
  const weekdaySelection = [...selectedWeekdays].map((weekday) => weekdays[weekday]);

  const onDateChange = useCallback(
    ({ range }: { range: Interval }) => {
      setDateRange(range);
      setSelectedWeekdays(availableWeekdays);
    },
    [availableWeekdays],
  );

  const { data: limits } = useLimits(marketLocation.id, isOpen ? device.id : undefined, dateRange);
  const powerLevels = usePowerLevels(device.id)?.map((level) => convertedValue(level) ?? 0);
  const installedPower = convertedValue(device.installedPower) ?? 0;

  const [production, setProduction] = useState<Measure>({ value: installedPower, unit: 'kW' });

  const [update] = useTemplatedForecastDataMutation();
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!limits) return;

    const interval = isInSliceSelectionMode
      ? { start: startOfDay(assistantDate), end: endOfDay(assistantDate) }
      : dateRange;

    const { minimum, maximum } = reduceForecastTemplateLimits(limits?.template, interval);
    const fields = initializeFields(
      [{ type: InputFieldType.PRODUCTION, value: production }],
      limits.fields,
      minimum,
      maximum,
      false,
    )
      .filter((field) => isMeasureWithValue(field.value))
      .map((field) => ({ type: field.type, value: field.value as Measure }));

    setSaving(true);
    try {
      await update(
        device.id,
        { ...interval, end: addSeconds(interval.end, 1) },
        Resolution.ONE_HOUR,
        isInSliceSelectionMode ? [weekdays[assistantDate.getDay() as DayOfWeek]] : weekdaySelection,
        isInSliceSelectionMode ? sliceSelection : hourSelection,
        fields,
        tableInterval,
      );
      close();
      showSuccess(t('FORECAST_WIZARD_SUCCESS'));
      if (isInSliceSelectionMode) toggleSelectionMode();
    } catch (error: unknown) {
      showError(t(getErrorTranslationKey(error)));
    } finally {
      setSaving(false);
    }
  };

  const sortedSliceSelection = [...sliceSelection].sort((a, b) => a - b).map((hour) => String(hour));
  const humanReadableSliceSelection = sortedSliceSelection
    .concat(sortedSliceSelection.splice(-2, 2).join(t('JOIN_CONCAT_COLLECTIVE')))
    .join(', ');

  const isValid =
    isInSliceSelectionMode ||
    (limits && !isBefore(dateRange.start, getMinDate(device.deadlines)) && hourCount > 0 && selectedWeekdays.size);

  return (
    <>
      <div
        aria-hidden
        className={clsx('fixed inset-0 z-[1300] transition-colors', isOpen ? 'bg-black/25' : 'pointer-events-none')}
        tabIndex={-1}
        onClick={close}
        onKeyDown={close}
      />

      {!!currentForecastProperty?.editableMarkets.length && (
        <div
          className={clsx(
            'fixed inset-x-0 bottom-0 z-[1200] flex items-center justify-between gap-x-4 bg-white p-4 shadow-[4px_0_4px_rgba(0,_0,_0,_0.25)] transition-transform duration-[225ms] [transition-timing-function:cubic-bezier(0,_0,_0.2,_1)]',
            { 'translate-y-[calc(100%+4px)]': !visible || isOpen },
          )}
        >
          <Button
            className="flex w-full items-center justify-center gap-2"
            disabled={isInSliceSelectionMode && !sliceSelection.length}
            onClick={open}
          >
            <EditIcon />
            {t(
              isInSliceSelectionMode ? 'FORECAST_ASSISTANT_BUTTON_LABEL_SLICE_MODE' : 'FORECAST_ASSISTANT_BUTTON_LABEL',
            )}
          </Button>
          {allowSliceSelection && (
            <label
              className={clsx(
                'relative box-border flex h-10 min-w-16 items-center justify-center rounded-[4px] border border-solid border-green-200/50 transition-colors hover:border-green-200',
                isInSliceSelectionMode ? 'bg-white text-green-200 shadow-inner' : 'bg-green-200 text-white',
              )}
              title={t('FORECAST_ASSISTANT_MOBILE_SELECT_SLICES')}
            >
              <CheckCircleIcon className="size-6" />
              <input
                type="checkbox"
                className="absolute inset-0 m-0 hidden appearance-none"
                checked={isInSliceSelectionMode}
                onChange={toggleSelectionMode}
              />
            </label>
          )}
        </div>
      )}

      <div
        className={clsx(
          'fixed inset-x-0 bottom-0 z-[1300] flex items-center justify-end rounded-t-xl bg-white shadow-[4px_0_4px_rgba(0,_0,_0,_0.25)] transition-transform duration-[225ms] [transition-timing-function:cubic-bezier(0,_0,_0.2,_1)]',
          { 'translate-y-[100vh]': !isOpen },
        )}
      >
        <section className="flex max-h-[calc(100vh-8rem)] w-full flex-col pt-5 sm:max-h-[calc(100vh-6rem)]">
          <div className="flex items-center justify-between px-4 text-base font-semibold leading-6">
            {t('FORECAST_ASSISTANT_BUTTON_LABEL')}
            <button
              className="m-0 appearance-none border-none bg-transparent p-0 text-base leading-none"
              onClick={close}
            >
              <ClearIcon />
            </button>
          </div>
          <hr className="mb-0 mt-1 w-full opacity-5" />
          <div className="mb-16 flex flex-col overflow-auto px-4 pb-5 pt-2">
            <h1 className="m-0 text-lg font-normal">{device.externalName || device.name}</h1>
            {isInSliceSelectionMode && (
              <div className="my-2 rounded-lg bg-grey-100 p-2 leading-none">
                <p className="m-0 flex items-center text-sm leading-6">
                  {t('FORECAST_ASSISTANT_MOBILE_PREVIEW', {
                    date: assistantDate.toLocaleDateString(locale),
                    hours: humanReadableSliceSelection,
                    production: production?.value,
                  })}
                </p>
              </div>
            )}
            {visible && !isInSliceSelectionMode && (
              // Hide the date picker on non-table views to keep Cypress from getting confused
              // and everything else too in general if assistant is in slice selection mode
              <>
                <h2 className="m-0 text-base font-normal">{t('FORECAST_ASSISTANT_DATE_RANGE')}</h2>
                <hr className="mb-0 mt-1 w-[calc(100%-2px)] opacity-5" />
                {isOpen && (
                  // Date picker needs to be rerendered to reset the initial range
                  <DateRangePicker
                    locale={de}
                    startOfWeek={1}
                    dateOnly
                    showOutsideDays
                    showWeekNumber
                    fixedWeeks
                    initialRange={dateRange}
                    disabled={{
                      before: getMinDate(device.deadlines),
                      after:
                        marketLocation.marketingEnd != null ? subSeconds(marketLocation.marketingEnd, 1) : undefined,
                    }}
                    onChange={onDateChange}
                  />
                )}
                <h2 className="m-0 text-base font-normal">{t('FORECAST_ASSISTANT_TIME_RANGE')}</h2>
                <hr className="mb-0 mt-1 w-[calc(100%-2px)] opacity-5" />
                <div className="my-4 flex items-center justify-evenly">
                  <TimePickerMobile
                    date={timeRange.start}
                    updateTime={setStart}
                    isFifteenMinutesResolution={isFifteenMinutesResolution}
                    isStartTime
                  />
                  –
                  <TimePickerMobile
                    date={timeRange.end}
                    updateTime={setEnd}
                    isFifteenMinutesResolution={isFifteenMinutesResolution}
                  />
                </div>
                <h2 className="m-0 text-base font-normal">{t('FORECAST_ASSISTANT_WEEKDAYS_MOBILE')}</h2>
                <hr className="mb-0 mt-1 w-[calc(100%-2px)] opacity-5" />
                <div className="my-4 grid grid-cols-7 gap-px">
                  {([1, 2, 3, 4, 5, 6, 0] as DayOfWeek[]).map((day) => (
                    <label
                      key={day}
                      className={clsx(
                        'relative p-2 text-center text-base leading-none first:rounded-l-lg last:rounded-r-lg',
                        selectedWeekdays.has(day) ? 'bg-green-200 text-white' : 'bg-grey-200',
                        availableWeekdays.has(day)
                          ? 'active:bg-gradient-to-r active:from-black/5 active:to-black/5'
                          : 'opacity-50',
                      )}
                    >
                      {setDay(new Date(), day).toLocaleDateString(locale, { weekday: 'short' })}
                      <input
                        type="checkbox"
                        className="absolute inset-0 m-0 hidden appearance-none"
                        checked={selectedWeekdays.has(day)}
                        disabled={!availableWeekdays.has(day)}
                        onChange={() =>
                          setSelectedWeekdays(
                            selectedWeekdays.has(day)
                              ? new Set([...selectedWeekdays].filter((weekday) => weekday !== day))
                              : new Set([...selectedWeekdays, day]),
                          )
                        }
                      />
                    </label>
                  ))}
                </div>
              </>
            )}
            <h2 className="m-0 text-base font-normal">{t('FORECAST_ASSISTANT_FORECAST_MOBILE')}</h2>
            <hr className="mb-0 mt-1 w-[calc(100%-2px)] opacity-5" />
            <ValuePickerMobile
              production={convertedValue(production) as number}
              installedPower={installedPower}
              powerLevels={powerLevels}
              selectValue={setProduction}
            />
          </div>
          <div className="fixed bottom-0 box-border w-full bg-white px-4 pb-4">
            <hr className="mb-4 mt-0 w-[calc(100%-2px)] opacity-5" />
            <div className="flex justify-between">
              <Button variant="outlined" onClick={close}>
                {t('SHARED_CANCEL')}
              </Button>
              <Button
                onClick={submit}
                disabled={!isValid || saving}
                startIcon={saving && <CircularProgress size={16} />}
              >
                {t('SHARED_SUBMIT')}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
